/**
 * Client access snapshot — the signed, locally-cached entitlement snapshot.
 *
 * What this is for (doc 43 + the client-access redesign):
 *   - After sign-in the server issues a signed snapshot of the caller's access
 *     facts (plan, features, permissions, role, scope, per-branch subscription
 *     plan codes, operating modes, capability packs). The client stores it in
 *     localStorage and refreshes it in the background (~60s), so route guards
 *     and nav can make instant decisions without waiting on the query chain.
 *   - The snapshot is Ed25519-signed by the server. The public key below is
 *     embedded in this bundle; the private key lives server-side only
 *     (`ACCESS_SNAPSHOT_PRIVATE_KEY`). A user cannot forge a snapshot — they can
 *     only replay an old one, which is why every consumer checks `issuedAt`
 *     freshness before trusting it.
 *
 * Honest boundary: this is tamper-EVIDENT, not tamper-proof, and it is a UX
 * accelerator only. Every real action is still authorized server-side (RLS +
 * route guards). A forged or stale snapshot can at worst delay or misdirect a
 * redirect; it can never grant an action the server would deny.
 *
 * This module is imported by both server (types only) and client (storage +
 * verification). All browser-only access is guarded so a server import never
 * touches `window`/`localStorage`/`crypto.subtle`.
 */

export const ACCESS_SNAPSHOT_VERSION = 1 as const;

/** How old a snapshot may be before consumers refuse to trust it. */
export const ACCESS_SNAPSHOT_TRUST_MS = 10 * 60 * 1000;

export const ACCESS_SNAPSHOT_STORAGE_KEY = 'menuraq-access-snapshot';

/**
 * Ed25519 public key (raw 32 bytes, base64) that verifies snapshots.
 * The matching private key is `ACCESS_SNAPSHOT_PRIVATE_KEY` (PKCS8 DER, base64)
 * in the server environment. Regenerate both together per environment.
 */
export const ACCESS_SNAPSHOT_PUBLIC_KEY_BASE64 =
  'OJNZ9g4l7V31/TIIMk2geISu02uNkIpNIJ2AmXPqwfo=';

export type AccessSnapshotBranch = {
  id: string;
  businessType: string | null;
  /** The branch's per-branch subscription plan code (doc 43). */
  planCode: 'core' | 'pro' | 'business' | null;
  subscribed: boolean;
  /** Operating-mode states for this branch (active/inactive/disabling). */
  modes: Record<string, 'active' | 'inactive' | 'disabling'>;
  /** Live capability-pack codes for this branch (doc 04). */
  packs: string[];
  /** branches.is_default — used to pick a branch on cold visits. */
  isDefault: boolean;
};

export type AccessSnapshotPayload = {
  v: typeof ACCESS_SNAPSHOT_VERSION;
  userId: string;
  businessId: string;
  issuedAt: string;
  /** businesses.permissions_version — bumped by every permission/role/scope write. */
  permissionsVersion: number;
  isOwner: boolean;
  role: string | null;
  permissions: Record<string, boolean> | null;
  scopeMode: 'all' | 'selected';
  scopeBranchIds: string[];
  offlinePos: boolean;
  /** Business-level plan code (fallback for unsubscribed branches). */
  businessPlanCode: string | null;
  /** Business-level feature flags (drives feature-triggered surface reveals). */
  features: Record<string, boolean>;
  branches: AccessSnapshotBranch[];
};

export type SignedAccessSnapshot = {
  payload: AccessSnapshotPayload;
  /** Ed25519 signature over `JSON.stringify(payload)`, base64. Null when the
   *  server has no signing key configured — clients must not cache those. */
  signature: string | null;
};

function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  if (typeof Buffer !== 'undefined') {
    const buf = Buffer.from(base64, 'base64');
    const out = new Uint8Array(new ArrayBuffer(buf.length));
    out.set(buf);
    return out;
  }
  const binary = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Verify the Ed25519 signature over the exact payload bytes the server signed.
 * JSON round-trips key order, so `JSON.stringify(parsedPayload)` reproduces the
 * signed bytes. Returns false on any failure (missing subtle crypto, bad
 * signature, malformed input) — consumers treat that as "no snapshot".
 *
 * `publicKeyBase64` defaults to the embedded production key; tests inject a
 * throwaway keypair to exercise the full verify path without the real key.
 */
export async function verifyAccessSnapshot(
  snapshot: SignedAccessSnapshot,
  publicKeyBase64: string = ACCESS_SNAPSHOT_PUBLIC_KEY_BASE64,
): Promise<boolean> {
  if (!snapshot?.payload || typeof snapshot.signature !== 'string') return false;
  if (snapshot.payload.v !== ACCESS_SNAPSHOT_VERSION) return false;
  if (typeof crypto === 'undefined' || !crypto.subtle) return false;

  try {
    const key = await crypto.subtle.importKey(
      'raw',
      base64ToBytes(publicKeyBase64),
      { name: 'Ed25519' },
      false,
      ['verify'],
    );
    const data = new TextEncoder().encode(JSON.stringify(snapshot.payload));
    return await crypto.subtle.verify('Ed25519', key, base64ToBytes(snapshot.signature), data);
  } catch {
    return false;
  }
}

/** True when the snapshot was issued recently enough to trust for a redirect. */
export function isSnapshotFresh(snapshot: SignedAccessSnapshot, now = Date.now()): boolean {
  const issuedAt = Date.parse(snapshot?.payload?.issuedAt ?? '');
  return Number.isFinite(issuedAt) && now - issuedAt <= ACCESS_SNAPSHOT_TRUST_MS;
}

/**
 * True when the snapshot's `permissionsVersion` is not behind the live one.
 *
 * Slice B of the client-access redesign: permission/role/scope/status writes
 * bump `businesses.permissions_version` (a monotonic counter; realtime →
 * business refetch → the client sees the new number in seconds). A snapshot
 * built before the bump is stale the moment the bump lands — consumers must
 * refuse it and fall back to the authoritative query path until the refetched
 * snapshot arrives.
 *
 * The comparison is directional: a snapshot AHEAD of the live version (the
 * snapshot refetched after a bump the business query has not seen yet) is
 * fresher, not stale — refusing it would disable the fast-path on the very
 * update that makes it most valuable.
 *
 * `livePermissionsVersion === null` means "unknown" (business not loaded yet) —
 * the snapshot is NOT refused, because refusing on an unknown would disable the
 * fast-path on every cold visit before the business query resolves.
 */
export function snapshotPermissionsCurrent(
  snapshot: SignedAccessSnapshot | null,
  livePermissionsVersion: number | null,
): boolean {
  if (!snapshot) return true;
  if (livePermissionsVersion === null) return true;
  return snapshot.payload.permissionsVersion >= livePermissionsVersion;
}

// ── Module-level cache ───────────────────────────────────────────────────────
// Verification is async (crypto.subtle), so the verified result is cached here
// after the first read. Consumers that need a synchronous answer on later
// renders read this; the first render of a cold visit falls back to the query
// path until verification completes (a few ms).

let cachedSnapshot: { key: string; snapshot: SignedAccessSnapshot } | null = null;

function cacheKey(userId: string, businessId: string): string {
  return `${userId}:${businessId}`;
}

/** Synchronous read of the last VERIFIED snapshot for this user/business. */
export function getCachedAccessSnapshot(
  userId: string,
  businessId: string,
): SignedAccessSnapshot | null {
  if (!cachedSnapshot) return null;
  if (cachedSnapshot.key !== cacheKey(userId, businessId)) return null;
  return cachedSnapshot.snapshot;
}

/**
 * Read + verify the stored snapshot for this user/business.
 * Returns null when absent, wrong user/business, stale version, or unverifiable.
 */
export async function readAccessSnapshot(
  userId: string,
  businessId: string,
): Promise<SignedAccessSnapshot | null> {
  if (!userId || !businessId) return null;
  const cached = getCachedAccessSnapshot(userId, businessId);
  if (cached) return cached;

  if (typeof window === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(ACCESS_SNAPSHOT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: SignedAccessSnapshot;
  try {
    parsed = JSON.parse(raw) as SignedAccessSnapshot;
  } catch {
    return null;
  }

  if (parsed?.payload?.userId !== userId || parsed?.payload?.businessId !== businessId) {
    return null;
  }
  if (!(await verifyAccessSnapshot(parsed))) return null;

  cachedSnapshot = { key: cacheKey(userId, businessId), snapshot: parsed };
  return parsed;
}

/** Persist a verified snapshot. Callers must verify BEFORE writing. */
export function writeAccessSnapshot(snapshot: SignedAccessSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACCESS_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage full / private mode — the in-memory cache still serves this tab.
  }
  cachedSnapshot = {
    key: cacheKey(snapshot.payload.userId, snapshot.payload.businessId),
    snapshot,
  };
}

export function clearAccessSnapshot(): void {
  cachedSnapshot = null;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(ACCESS_SNAPSHOT_STORAGE_KEY);
  } catch {
    // ignore
  }
}